import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { ArrowLeft, ShoppingBag, Gauge, MonitorPlay, GraduationCap, Info } from "lucide-react";
import { useLang } from "@/i18n";
import AffiliateCard from "@/components/site/AffiliateCard";
import Footer from "@/components/site/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { id: "equipement", icon: ShoppingBag },
  { id: "performance", icon: Gauge },
  { id: "logiciel", icon: MonitorPlay },
  { id: "formation", icon: GraduationCap },
];

export default function RessourcesPage() {
  const { t } = useLang();
  const [items, setItems] = useState([]);

  useEffect(() => {
    window.scrollTo(0, 0);
    axios.get(`${API}/affiliates`).then((r) => setItems(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="ressources-page" className="grain bg-[#0A0A0C] text-white min-h-screen">
      <header className="border-b border-[#27272A] bg-[#0A0A0C]/80 backdrop-blur-md sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-[72px] flex items-center justify-between">
          <Link to="/" data-testid="ressources-back" className="flex items-center gap-3 text-[#A1A1AA] hover:text-[#C5A059] transition-colors text-xs uppercase tracking-[0.2em]">
            <ArrowLeft size={15} /> {t("eventpage.back")}
          </Link>
          <img src="/assets/logo-dark-sm.png" alt="DREAM2REALITY Football Agency" className="h-10 w-auto" />
        </div>
      </header>

      <main>
        <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-24 pb-16">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <p className="text-[#C5A059] text-xs uppercase tracking-[0.4em] mb-6">{t("aff.page.eyebrow")}</p>
            <h1 className="font-display text-5xl lg:text-7xl uppercase font-bold leading-[0.95]">
              {t("aff.page.title.a")} <span className="text-[#C5A059]">{t("aff.page.title.b")}</span>
            </h1>
            <p className="text-base text-[#A1A1AA] mt-6 max-w-2xl leading-relaxed">{t("aff.page.text")}</p>
          </motion.div>

          <div className="flex items-start gap-3 border border-[#C5A059]/30 bg-[#C5A059]/5 p-5 mt-10" data-testid="ressources-disclosure">
            <Info size={16} className="text-[#C5A059] shrink-0 mt-0.5" />
            <p className="text-xs text-[#A1A1AA] leading-relaxed">{t("aff.disclosure")}</p>
          </div>
        </section>

        {CATEGORIES.map((cat) => {
          const list = items.filter((a) => a.categorie === cat.id);
          if (!list.length) return null;
          return (
            <section key={cat.id} data-testid={`ressources-cat-${cat.id}`} className="max-w-7xl mx-auto px-6 lg:px-10 pb-20">
              <div className="flex items-center gap-4 mb-8 border-b border-[#27272A] pb-5">
                <cat.icon size={20} className="text-[#C5A059]" />
                <h2 className="font-display text-2xl lg:text-3xl uppercase font-bold">{t(`aff.cat.${cat.id}`)}</h2>
                <span className="text-xs text-[#71717A] ml-auto">{list.length}</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {list.map((a, i) => (
                  <motion.div
                    key={a.id}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: (i % 4) * 0.06 }}
                  >
                    <AffiliateCard aff={a} />
                  </motion.div>
                ))}
              </div>
            </section>
          );
        })}
      </main>
      <Footer />
    </div>
  );
}
